import {
  FilterQuery,
  HierarchyCard,
  Pagination,
  SkeletonDots,
  SortOption,
  YesObject,
  feedbackcard,
  useDisplayUserError
} from '@yes.technology/react-toolkit'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { PatchObjectResponse } from 'shared/types/object.type'
import { ScrollToHashElement } from 'shared/utils'
import { concatenateFields } from 'shared/utils/object/concatenateFields'
import useStyleMaps from 'site/Renderer/hooks/useStyleMap'
import { useSiteState } from 'siteState/shared'
import { useSitemodel } from 'sitemodel/shared/hooks'
import StatusCard from 'status/StatusCard/StatusCard'
import { updateStatus, useStatusActions, useStatuses } from 'status/shared'
import {
  useFetchYesObjects,
  usePopulateObjectRelations
} from 'yesObject/shared'
import useYesObject from 'yesObject/shared/hooks/useYesObject'
import useCancelChanges from '../EditValues/CancelModal/useCancelChanges'
import useEditValues, {
  OBJECT_VIEW_NAVIGATION_CALLBACK_KEY
} from '../EditValues/useEditValues'
import SectionDivider from '../SectionDivider'
import Fields from './components/Fields/Fields'
import NavigationBetweenObjects from './components/NavigationBetweenObjects/NavigationBetweenObjects'
import ObjectViewReports from './components/ObjectViewReports/ObjectViewReports'
import RelationClasses from './components/RelationClasses/RelationClasses'
import ShowSituation from './components/ShowSituation/ShowSituation'
import { ObjectViewProps } from './components/types/object-view'
import useObjectFields from './hooks/useObjectFields'
import useOnSaveObject from './hooks/useOnSaveObject/useOnSaveObject'
import useObjectFromSource from './lib/selectObjectSource/useObjectFromSource'

type LocationState = {
  objectIndex?: number
  sortOptions: SortOption[]
  objectclassUuid: string
  pagination?: Pagination
  shouldFetchFromUuid?: boolean
}

const ObjectView = ({ props }: ObjectViewProps) => {
  const {
    relations_view: relationConfig,
    uuid_classification: classificationUuid,
    uuid_objectclass_query: objectclassUuidQueryKey = 'uuid_objectclass',
    informational = false,
    concat_fields: concatFields,
    field_settings: fieldSettings,
    nav_sticky: navSticky = false,
    styleMap,
    object_uuid,
    is_show_reports: isShowReports,
    enable_label_fading: enableLabelFading = true,
    enable_value_fading: enableValueFading = true,
    relations_field_type: defaultFieldType = 'table'
  } = props

  const sitemodel = useSitemodel()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const locationState = location?.state as LocationState

  const [filterQuery] = useSiteState<FilterQuery | undefined>('filter-query')
  const [objectIndex, setObjectIndex] = useSiteState<number | undefined>(
    'object-index'
  )
  const [_, setLockObjectViewObject] = useSiteState<boolean>(
    'lock-object-view-object'
  )

  const [__, setRefetchFilterQuery] = useSiteState('refetch-filterquery')

  const [refetchObjectView, setRefetchObjectView] =
    useSiteState<boolean>('refetch-objectview')

  const [, setDisableDynamicElementScroll] = useSiteState<boolean>(
    'disable-dynamic-element-scroll'
  )

  const styleProps = useStyleMaps(styleMap)

  const [shouldUpdateURL, setShouldUpdateURL] = useState(false)

  const objectUuid = searchParams.get('object-uuid') || object_uuid || ''
  const objectclassUuid = searchParams.get(objectclassUuidQueryKey) || ''

  const objectFields = useObjectFields({
    objectclassUuid,
    classificationUuid,
    fieldSettings
  })

  const { displayUserError } = useDisplayUserError()

  const { sortOptions, shouldFetchFromUuid } = locationState || {}

  const hasObjectIndex = typeof objectIndex === 'number'

  const hasValidIndexSelection = !!filterQuery && hasObjectIndex

  const shouldFetchFromIndex = !shouldFetchFromUuid && hasValidIndexSelection

  const {
    yesObject: objectFromUuid,
    isFetching: isFetchingObjectFromUuid,
    fetchObjects: fetchObjectFromUuid
  } = useYesObject({
    objectUUID: objectUuid,
    objectclassUUID: objectclassUuid,
    enabled: !shouldFetchFromIndex
  })

  const {
    yesObjects: [objectFromIndex],
    isFetching: isFetchingObjectFromIndex,
    pagination,
    setPage,
    setSortOptions,
    fetchYesObjects: fetchObjectFromIndex
  } = useFetchYesObjects({
    filterBody: {
      query: filterQuery || {},
      objectclass: objectclassUuid
    },
    initialSortOptions: sortOptions,
    initialPagination: { limit: 1, offset: objectIndex || 0 },
    enabled: shouldFetchFromIndex
  })

  const { yesObject, isFetchingObject, refetchObject } = useObjectFromSource({
    fetchObjectFromIndex,
    fetchObjectFromUuid,
    isFetchingObjectFromIndex,
    isFetchingObjectFromUuid,
    objectFromIndex,
    objectFromUuid,
    objectOrigin: shouldFetchFromIndex ? 'index' : 'uuid'
  })

  useEffect(() => {
    if (!hasValidIndexSelection || objectFromIndex) return

    fetchObjectFromIndex()
  }, [objectFromIndex, hasValidIndexSelection, fetchObjectFromIndex])

  const isFetching = isFetchingObject || objectFields.isLoading

  useEffect(() => {
    if (hasObjectIndex) setPage(objectIndex + 1)
  }, [hasObjectIndex, objectIndex, setPage])

  useEffect(() => {
    if (refetchObjectView) {
      setRefetchObjectView(false)
      refetchObject()
    }
  }, [refetchObjectView, refetchObject, setRefetchObjectView])

  useEffect(() => {
    setSortOptions(sortOptions)
  }, [sortOptions, setSortOptions])

  useEffect(() => {
    setDisableDynamicElementScroll(true)
  }, [setDisableDynamicElementScroll])

  const updateUuidInUrl = () => {
    if (!yesObject?.uuid || yesObject.uuid === objectUuid || !shouldUpdateURL)
      return

    setShouldUpdateURL(false)
    searchParams.set('object-uuid', yesObject.uuid)
    navigate(`${location.pathname}?${searchParams}#object-view`, {
      replace: true,
      state: location.state
    })
  }

  useEffect(updateUuidInUrl, [
    yesObject?.uuid,
    objectUuid,
    searchParams,
    location,
    navigate,
    shouldUpdateURL
  ])

  const statuses = useStatuses({ objectclassUuid })
  const { statusActions } = useStatusActions({ statuses })

  const handlePatchSuccess = useCallback(() => {
    feedbackcard(t('data-tables.success'), {
      type: 'success',
      message: t('data-tables.success')
    })
    setRefetchFilterQuery(true)
    refetchObject()
  }, [t, setRefetchFilterQuery, refetchObject])

  const handlePatchError = useCallback(
    (response?: PatchObjectResponse) => {
      displayUserError({
        error: response,
        title: t('data-tables.error'),
        userMessageFallback: t('data-tables.error')
      })
    },
    [displayUserError, t]
  )

  const onSave = useOnSaveObject({
    objectUuid,
    objectclassUuid,
    version: yesObject?.version,
    onSuccess: handlePatchSuccess,
    onError: handlePatchError
  })

  const {
    fieldValues,
    isEditing,
    onChange,
    edit,
    cancel,
    cancelChanges,
    saveChanges,
    persistChanges,
    changeAndSave,
    addOnSuccessCallback
  } = useEditValues({
    fields: objectFields.fields,
    yesObject,
    onSave
  })

  const { confirmIfPendingChanges } = useCancelChanges()

  const [contentMinHeight, setContentMinHeight] = useState(0)
  const updateContentMinHeightFromDom = useCallback(
    (container: HTMLDivElement | null) => {
      if (container?.offsetHeight) {
        setContentMinHeight((prev) =>
          prev !== container.offsetHeight ? container.offsetHeight : prev
        )
      }
    },
    []
  )

  const isYesObjectEmpty = !isFetching && !yesObject
  const isObjectFieldsEmpty = !isFetching && !objectFields?.fields?.length

  const sitemodelTitle = sitemodel?.title
  const objectclassName = objectFields?.objectclass?.des
  const title = sitemodelTitle || objectclassName || ''
  const completeTitle = title
    ? `${title} | ${t('area-title.object-view')}`
    : t('area-title.object-view')

  const values = isEditing ? fieldValues : yesObject

  const concatenatedFields = concatFields
    ? concatenateFields(concatFields, values as YesObject)
    : values

  const { data } = usePopulateObjectRelations({
    objects: concatenatedFields ? [concatenatedFields] : [],
    relationFields: objectFields.relationFields,
    enabled: true
  })

  const handleNavigation = useCallback(
    (newPage: number) => {
      if (isEditing) {
        addOnSuccessCallback(OBJECT_VIEW_NAVIGATION_CALLBACK_KEY, () => {
          cancelChanges()
          setObjectIndex(newPage - 1)
          setShouldUpdateURL(true)
          setLockObjectViewObject(true)
        })
      } else {
        setObjectIndex(newPage - 1)
        setShouldUpdateURL(true)
        setLockObjectViewObject(true)
      }

      navigate(`${location.pathname}${location.search}#object-view`, {
        replace: true,
        state: { ...location.state, ...{ shouldFetchFromUuid: false } }
      })
    },
    [
      location.pathname,
      location.search,
      location.state,
      navigate,
      setLockObjectViewObject,
      setObjectIndex,
      isEditing,
      cancelChanges,
      addOnSuccessCallback
    ]
  )

  const navigateCancelGuard = (newPage: number) => {
    confirmIfPendingChanges(() => handleNavigation(newPage))
  }

  if (isYesObjectEmpty || isObjectFieldsEmpty)
    return (
      <SectionDivider
        navSticky={navSticky}
        id='object-view'
        title={completeTitle}
        centerHeaderElement={
          hasObjectIndex ? (
            <NavigationBetweenObjects
              currentPosition={objectIndex}
              pagination={pagination}
              onNavigating={navigateCancelGuard}
            />
          ) : undefined
        }
        backgroundColor='#004099'
        disabled
        style={styleProps}
      />
    )

  return (
    <SectionDivider
      navSticky={navSticky}
      id='object-view'
      title={completeTitle}
      centerHeaderElement={
        hasObjectIndex ? (
          <NavigationBetweenObjects
            currentPosition={objectIndex}
            pagination={pagination}
            onNavigating={navigateCancelGuard}
          />
        ) : undefined
      }
      rightHeaderElement={
        <ObjectViewReports
          {...{
            isFetching,
            yesObject: data[0],
            filterQuery,
            informational,
            relationConfig,
            fieldSettings,
            concatFields,
            classificationUuid,
            isShowReports
          }}
        />
      }
      backgroundColor='#004099'
      style={styleProps}
    >
      <HierarchyCard className='pb-4'>
        {isFetching && (
          <div style={{ minHeight: contentMinHeight }}>
            <SkeletonDots />
          </div>
        )}

        {!isFetching && (
          <div ref={updateContentMinHeightFromDom} className='p-0'>
            <ScrollToHashElement />
            {!informational && yesObject?.uuid && yesObject?.version && (
              <div className='row g-2'>
                <StatusCard
                  informational={informational}
                  statusUuid={yesObject?.uuid_status || ''}
                  updateStatus={(destinationStatusCode) =>
                    updateStatus({
                      objectVersion: yesObject.version,
                      objectUuid: yesObject.uuid,
                      objectclassUuid,
                      destinationStatusCode,
                      showLoading: true
                    }).then(refetchObject)
                  }
                  {...{
                    statusActions,
                    statuses
                  }}
                />

                <ShowSituation
                  yesObject={yesObject}
                  situationCode={yesObject?.situation}
                  objectClassUUID={objectclassUuid}
                  objectUUID={yesObject.uuid}
                  objectVersion={yesObject.version}
                  onRefresh={refetchObject}
                  onEditCancel={cancel}
                  mainActionLabel={
                    isEditing
                      ? t('data-tables.save-and-activate')
                      : t('data-tables.modify')
                  }
                  mainActionOnClick={isEditing ? persistChanges : edit}
                  informational={informational}
                  isEditing={isEditing}
                />
              </div>
            )}

            <Fields
              fields={objectFields.fields}
              informational={informational || !isEditing}
              fieldValues={concatenatedFields}
              onChange={onChange}
              isEditing={isEditing}
              enableLabelFading={enableLabelFading}
              enableValueFading={enableValueFading}
            />

            <RelationClasses
              relationFields={objectFields.relationFields}
              fieldValues={yesObject}
              save={saveChanges}
              {...{
                edit,
                changeAndSave,
                onChange,
                isEditing,
                informational,
                relationConfig,
                defaultFieldType
              }}
            />
          </div>
        )}
      </HierarchyCard>
    </SectionDivider>
  )
}

export default ObjectView
